import { Link, useLocation } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import {
  LayoutDashboard,
  ChevronDown,
  ChevronRight,
  Menu,
  Building2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import api from "@/services/api";
import { authService } from "@/services/authService";
import { eaService } from "@/services/eaService";
import documentRequestService from "@/services/documentRequestService";
import projectService from "@/services/projectService";
import { taskOrderService } from "@/services/taskOrderService";
import complianceMaturityService from "@/services/complianceMaturityService";
import legalDeadlineService from "@/services/legalDeadlineService";
import proposalService from "@/services/proposalService";
import performanceDistributionService from "@/services/performanceDistributionService";
import leavePromotionService from "@/services/leavePromotionService";
import offboardingService from "@/services/offboardingService";
import disciplineService from "@/services/disciplineService";
import { worklogService } from "@/services/worklogService";
import { businessTripService } from "@/services/businessTripService";
import { Badge } from "@/components/ui/badge";
import type { MenuItem } from "@/types/admin";
import { MenuSearchBar } from "@/components/MenuSearchBar";
import { menuSearchService } from "@/services/menuSearchService";
import type { MenuSearchResult } from "@/types/menuSearch";

interface NavItem {
  id?: number; // 시스템 메뉴 id (검색 클릭 로그용)
  title: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
  requiredRoles?: string[];
  isActive?: boolean; // 메뉴 활성화 상태 추가
}

// 아이콘 이름을 컴포넌트로 변환
const getIconComponent = (iconName?: string): React.ComponentType<{ className?: string }> => {
  if (!iconName) return Menu;
  const Icon = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[iconName];
  return Icon || Menu;
};

// MenuItem을 NavItem으로 변환 (비활성 메뉴는 사이드바에서 제외)
const convertMenuToNavItem = (menu: MenuItem): NavItem => {
  const navItem: NavItem = {
    id: typeof menu.id === "number" ? menu.id : undefined,
    title: menu.name,
    href: menu.path || undefined,
    icon: getIconComponent(menu.icon),
    requiredRoles: menu.required_roles || [],
    isActive: menu.is_active,
  };

  if (menu.children && menu.children.length > 0) {
    navItem.children = menu.children
      .filter((child) => child.is_active !== false)
      .sort((a, b) => a.order_num - b.order_num)
      .map(convertMenuToNavItem);
  }

  return navItem;
};

// 폴백 정적 메뉴 (API 실패 시 사용)
const fallbackNavItems: NavItem[] = [
  {
    title: "대시보드",
    href: "/",
    icon: LayoutDashboard,
  },
];

// 권한 레벨 정의
const ROLE_HIERARCHY: Record<string, number> = {
  super_admin: 4,
  admin: 3,
  dept_leader: 2,
  staff: 1,
  customer: 0,
};

// 권한 뱃지 정보
interface RoleBadgeInfo {
  label: string;
  variant: "destructive" | "default" | "secondary" | "outline";
  className?: string;
}

// 권한별 뱃지 정보 반환
const getRoleBadgeInfo = (requiredRoles?: string[]): RoleBadgeInfo | null => {
  if (!requiredRoles || requiredRoles.length === 0) {
    return null; // 일반 권한은 뱃지 표시 안 함
  }

  // 가장 높은 권한 찾기
  const highestRole = requiredRoles.reduce((highest, role) => {
    const currentLevel = ROLE_HIERARCHY[role] || 0;
    const highestLevel = ROLE_HIERARCHY[highest] || 0;
    return currentLevel > highestLevel ? role : highest;
  }, requiredRoles[0]);

  switch (highestRole) {
    case "super_admin":
      return { label: "S", variant: "destructive" };
    case "admin":
      return { label: "A", variant: "default", className: "bg-purple-100 text-purple-700 hover:bg-purple-100" };
    case "dept_leader":
      return { label: "L", variant: "default", className: "bg-blue-100 text-blue-700 hover:bg-blue-100" };
    case "customer":
      return { label: "C", variant: "default", className: "bg-green-100 text-green-700 hover:bg-green-100" };
    case "staff":
      return null; // 일반 권한은 뱃지 표시 안 함
    default:
      return null;
  }
};

export default function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, setSidebarOpen } = useUIStore();
  const { isAuthenticated, getDefaultLandingPath } = useAuthStore();
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("HubCore");
  // 메뉴 검색: 검색어(로컬 이름 매칭) + 시맨틱 검색 결과(menu_id)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MenuSearchResult[]>([]);
  const searchTerm = searchQuery.trim().toLowerCase();
  const isSearching = searchTerm.length > 0;

  // 사용자 접근 가능 메뉴 조회
  const { data: userMenus } = useQuery({
    queryKey: ["user-menus"],
    queryFn: authService.getMyMenus,
    enabled: isAuthenticated,
    staleTime: 0, // 항상 최신 데이터 사용
    gcTime: 1000 * 60, // 1분간 가비지 컬렉션 대기
  });

  // userMenus(트리)에서 특정 menu code 보유 여부 확인
  const hasMenuAccess = useCallback(
    (code: string): boolean => {
      if (!userMenus) return false;
      const visit = (items: MenuItem[]): boolean =>
        items.some((m) => m.code === code || (m.children && visit(m.children)));
      return visit(userMenus);
    },
    [userMenus],
  );

  // 메뉴 검색 변경 핸들러 (검색어 + 시맨틱 결과)
  const handleSearchChange = useCallback((query: string, results: MenuSearchResult[]) => {
    setSearchQuery(query);
    setSearchResults(results);
  }, []);

  // 사이드바 접기 시 검색창이 사라지므로 필터도 해제 (필터가 남아 메뉴가 가려지는 것 방지)
  useEffect(() => {
    if (sidebarCollapsed) {
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [sidebarCollapsed]);

  // 기업 로고 정보 조회 (localStorage에서 가져오기)
  useEffect(() => {
    const updateCompanyInfo = () => {
      const storedCompanyName = localStorage.getItem("company_name");
      const storedCompanyLogo = localStorage.getItem("company_logo");

      console.log('[Sidebar] Company branding:', { storedCompanyName, storedCompanyLogo });

      // MinIO presigned URL이면 제거 (API 엔드포인트만 허용)
      if (storedCompanyLogo && (
        storedCompanyLogo.includes('X-Amz-Algorithm') ||
        storedCompanyLogo.includes('192.168.7.172:10000') ||
        storedCompanyLogo.includes('/storage/')
      )) {
        console.log('[Sidebar] Removing old MinIO URL from localStorage');
        localStorage.removeItem("company_logo");
        setCompanyLogo(null);
      } else if (storedCompanyLogo) {
        setCompanyLogo(storedCompanyLogo);
      }

      if (storedCompanyName) setCompanyName(storedCompanyName);
    };

    // 커스텀 이벤트 핸들러 (기업 전환 시)
    const handleBrandingUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[Sidebar] Branding updated via custom event:', customEvent.detail);

      if (customEvent.detail?.company_name) {
        setCompanyName(customEvent.detail.company_name);
      }
      if (customEvent.detail?.company_logo) {
        setCompanyLogo(customEvent.detail.company_logo);
      } else {
        setCompanyLogo(null);
      }
    };

    // 초기 로드
    updateCompanyInfo();

    // 이벤트 리스너 추가
    window.addEventListener('storage', updateCompanyInfo); // 다른 탭에서 localStorage 변경 시
    window.addEventListener('company-branding-updated', handleBrandingUpdate); // 같은 탭에서 기업 전환 시

    return () => {
      window.removeEventListener('storage', updateCompanyInfo);
      window.removeEventListener('company-branding-updated', handleBrandingUpdate);
    };
  }, []);

  // 전자결재(EA) 배지: 받은 결재·지출 결재 처리 대기 건수
  const { data: eaCounts } = useQuery({
    queryKey: ["ea", "counts"],
    queryFn: eaService.getInboxCounts,
    enabled: isAuthenticated,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // 요청 서류 대기 건수 조회 (페이지 로드 시에만)
  const { data: docRequestPendingCount } = useQuery({
    queryKey: ["document-request-pending-count"],
    queryFn: () => documentRequestService.getReceivedPendingCount(),
    enabled: isAuthenticated && hasMenuAccess("document-requests-my"),
  });

  // 업무 하달 - 본인에게 와서 처리(액션)가 필요한 건수 조회
  // 1) 요청됨(수락/거절 대기)  2) 수락했으나 칸반 미등록
  const { data: receivedTaskOrders } = useQuery({
    queryKey: ["task-order-received-pending"],
    queryFn: () => taskOrderService.getReceivedTaskOrders(),
    enabled: isAuthenticated,
  });
  const receivedActionableCount = useMemo(() => {
    if (!receivedTaskOrders) return 0;
    // 본인이 현재 보유자인 건 중 처리(액션)가 필요한 상태만 카운트
    // (RECALLED/CANCELLED/REJECTED/KANBAN_REGISTERED/IN_PROGRESS/COMPLETED 등은 제외)
    return receivedTaskOrders.filter((t) => {
      if (t.status === "REQUESTED") return true;
      // 수락했지만 아직 칸반에 등록하지 않은 건
      if (t.status === "ACKNOWLEDGED" && !t.linked_kanban_card_id) return true;
      return false;
    }).length;
  }, [receivedTaskOrders]);

  // 업무 하달 - 원지시자(본인)가 검토해야 하는 건수 (검토 대기)
  const { data: originatedReviewOrders } = useQuery({
    queryKey: ["task-order-review-pending"],
    queryFn: () => taskOrderService.getOriginatedTaskOrders("PENDING_REVIEW"),
    enabled: isAuthenticated,
  });
  const reviewPendingCount = originatedReviewOrders?.length ?? 0;

  // HR 가입 승인 대기 건수 조회 (배지는 /hr/employees 메뉴에 표시).
  // 가입승인 화면이 /hr/employees 탭으로 통합되어 별도 'user-approval' 메뉴는 제거됨 →
  // 권한 조건도 통합된 페이지 메뉴('hr_employees')를 따른다(백엔드 게이트와 일치).
  const { data: hrApprovalStats } = useQuery({
    queryKey: ["hr-approval-stats"],
    queryFn: async () => {
      const response = await api.get("/user-approval/stats");
      return response.data as { pending_count: number; approved_count: number; rejected_count: number };
    },
    enabled: isAuthenticated && hasMenuAccess("hr_employees"),
  });

  // 성장 로드맵 - 미이행 의무 건수 (compliance-roadmap 메뉴 권한 보유 시에만)
  const { data: complianceRoadmapPending } = useQuery({
    queryKey: ["compliance-roadmap-pending"],
    queryFn: () => complianceMaturityService.pendingCount(),
    enabled: isAuthenticated && hasMenuAccess("compliance-roadmap"),
  });

  // 법정기한 관리 - 초과 + 7일 내 임박 건수 (compliance-deadlines 메뉴 권한 보유 시에만)
  const { data: legalDeadlinePending } = useQuery({
    queryKey: ["legal-deadline-pending-count"],
    queryFn: () => legalDeadlineService.pendingCount(),
    enabled: isAuthenticated && hasMenuAccess("compliance-deadlines"),
  });

  // 성과 배분 미생성 건수 (admin_performance 메뉴 권한 보유 시에만)
  const { data: performanceDistributionPending } = useQuery({
    queryKey: ["performance-distribution-pending-count"],
    queryFn: () => performanceDistributionService.pendingCount(),
    enabled: isAuthenticated && hasMenuAccess("admin_performance"),
  });

  // 제안 작업 - 내 임박 페이지 + (총괄) 확정 대기 (proposal-projects 메뉴 권한 보유 시에만)
  const { data: proposalPending } = useQuery({
    queryKey: ["proposal-pending-count"],
    queryFn: () => proposalService.pendingCount(),
    enabled: isAuthenticated && hasMenuAccess("proposal-projects"),
  });

  // 일일 업무 보고 - 미등록 건수 (daily-worklog 메뉴 권한 보유 시에만). 보고 저장 시 invalidate 되어 즉시 갱신
  const { data: dailyReportStatus } = useQuery({
    queryKey: ["daily-report-status"],
    queryFn: () => worklogService.getRequiredStatus(),
    enabled: isAuthenticated && hasMenuAccess("daily-worklog"),
    staleTime: 5 * 60 * 1000,
  });

  // 연차사용촉진 통지 - 대응 필요 건수 (휴가 메뉴에 배지 표시).
  // 휴가 화면(LeavePromotionNotices)과 동일한 쿼리키를 공유하므로
  // 계획 제출(reply)·수신확인(acknowledge) 시 invalidate되어 배지가 즉시 갱신된다.
  const { data: myLeavePromotions } = useQuery({
    queryKey: ["my-leave-promotions"],
    queryFn: () => leavePromotionService.myNotices(),
    enabled: isAuthenticated,
  });
  const leavePromotionActionCount = useMemo(() => {
    if (!myLeavePromotions) return 0;
    const currentYear = new Date().getFullYear();
    return myLeavePromotions.filter((n) =>
      // 1차 촉구: 사용 계획 미제출 건 / 2차 지정: 당해년도 미확인 건 (화면 열람 시 자동 수신확인)
      n.stage === 1 ? !n.employee_plan : n.year >= currentYear && !n.read_at,
    ).length;
  }, [myLeavePromotions]);

  // 퇴사 관리 - 승인 대기 케이스 수 (offboarding-admin 메뉴 권한 보유 시에만)
  const { data: offboardingAdminPending } = useQuery({
    queryKey: ["offboarding-admin-pending"],
    queryFn: () => offboardingService.getAdminPendingCount(),
    enabled: isAuthenticated && hasMenuAccess("offboarding-admin"),
  });

  // 내 징계 내역 - 본인이 경위서 등 서류를 제출해야 하는 건수 (discipline-my 메뉴 보유 시에만).
  // MyDisciplinePage/상세와 동일 기준이며, 제출·저장 시 해당 화면에서 invalidate되어 즉시 줄어든다.
  const { data: disciplineMyPending } = useQuery({
    queryKey: ["discipline-my-pending-count"],
    queryFn: () => disciplineService.myPendingCount(),
    enabled: isAuthenticated && hasMenuAccess("discipline-my"),
  });

  // 출장 관리 - 결과보고서 미작성 건수 (출장 관리 페이지 "보고서 미작성" 탭과 동일 범위:
  // 관리자는 회사 전체, 그 외는 본인 참여 건만 — 범위는 백엔드가 결정).
  // queryKey 를 ["business-trips", ...] 프리픽스로 두어 출장 페이지의 기존
  // invalidate/refetch(["business-trips"]) 가 보고서 작성 직후 배지도 함께 갱신한다.
  const { data: businessTripPendingReports } = useQuery({
    queryKey: ["business-trips", "pending-reports", "count"],
    queryFn: () => businessTripService.getPendingReportCount(true),
    enabled: isAuthenticated && hasMenuAccess("business-trip"),
  });

  // 퇴사·인수인계(offboarding-my)는 사이드바 상시 메뉴에서 내리고 프로필 진입점 +
  // 상단 배너(OffboardingBanner: 당사자 진행중·반려 + 받은 인수인계)로 대체됨 → 여기 배지 없음.

  // SLA 면책 요청 대기 건수 (페이지 로드 시에만)
  const { data: slaExemptionRequests } = useQuery({
    queryKey: ["sla-exemption-requests"],
    queryFn: () => projectService.getSLAExemptionRequests(),
    enabled: isAuthenticated,
  });

  // 복권 서비스 활성화 여부 조회
  const { data: lotteryStatus } = useQuery({
    queryKey: ["lottery-status"],
    queryFn: async () => {
      const response = await api.get("/lottery/status");
      return response.data as { is_enabled: boolean };
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 10,
  });

  // 시맨틱 검색 결과 menu_id 집합
  const searchMenuIds = useMemo(
    () => new Set(searchResults.map((r) => r.menu_id)),
    [searchResults],
  );

  // 메뉴 자체가 검색어에 매칭되는지 (이름/코드 부분일치 or 시맨틱 결과 포함)
  const matchesSearch = useCallback(
    (menu: MenuItem): boolean => {
      if (!isSearching) return true;
      if (menu.name?.toLowerCase().includes(searchTerm)) return true;
      if (menu.code?.toLowerCase().includes(searchTerm)) return true;
      return typeof menu.id === "number" && searchMenuIds.has(menu.id);
    },
    [isSearching, searchTerm, searchMenuIds],
  );

  // 검색어로 메뉴 트리를 가지치기 (깊이 무관)
  // - 매칭된 메뉴: 하위 전체를 그대로 유지
  // - 매칭 안 된 메뉴: 하위에 매칭이 있으면 "경로 유지용 상위 메뉴"로 남김
  const filterMenuTree = useCallback(
    (menus: MenuItem[]): MenuItem[] => {
      const result: MenuItem[] = [];
      for (const menu of menus) {
        const activeChildren = (menu.children || []).filter((c) => c.is_active !== false);
        if (matchesSearch(menu)) {
          result.push(menu);
          continue;
        }
        const matchedChildren = filterMenuTree(activeChildren);
        if (matchedChildren.length > 0) {
          result.push({ ...menu, children: matchedChildren });
        }
      }
      return result;
    },
    [matchesSearch],
  );

  // MenuItem을 NavItem으로 변환 (검색 시 필터링 적용)
  const navItems = useMemo(() => {
    if (!userMenus || userMenus.length === 0) {
      return fallbackNavItems;
    }

    const lotteryEnabled = lotteryStatus?.is_enabled !== false;

    let menus = userMenus
      .filter((menu) => menu.is_active !== false) // 비활성 메뉴는 사이드바에서 숨김
      .sort((a, b) => a.order_num - b.order_num);

    // 검색 중이면 트리를 필터링 (상위 메뉴는 자식이 매칭되면 함께 노출)
    if (isSearching) {
      menus = filterMenuTree(menus);
    }

    return menus
      .map((menu) => {
        const navItem = convertMenuToNavItem(menu);

        // 복권 서비스 OFF 시 하위 /lottery 메뉴 숨김
        if (!lotteryEnabled && navItem.children) {
          navItem.children = navItem.children.filter((child) => child.href !== "/lottery");
        }
        return navItem;
      })
      .filter((navItem) => {
        // 복권 서비스 OFF 시 최상위 /lottery 메뉴 숨김
        if (!lotteryEnabled && navItem.href === "/lottery") return false;
        return true;
      });
  }, [userMenus, lotteryStatus, isSearching, filterMenuTree]);

  // 검색 중 자동 펼침 대상: 필터 결과에 남은 모든 상위 메뉴 (깊이 무관)
  const autoOpenTitles = useMemo(() => {
    if (!isSearching) return [] as string[];
    const titles: string[] = [];
    const visit = (items: NavItem[]) => {
      items.forEach((item) => {
        if (item.children && item.children.length > 0) {
          titles.push(item.title);
          visit(item.children);
        }
      });
    };
    visit(navItems);
    return titles;
  }, [isSearching, navItems]);

  // 검색 시작/변경 → 상위 메뉴 자동 펼침, 검색 종료 → 접기
  const autoOpenKey = autoOpenTitles.join("|");
  useEffect(() => {
    if (!isSearching) {
      setOpenMenus((prev) => (prev.length > 0 ? [] : prev));
      return;
    }
    setOpenMenus(autoOpenKey ? autoOpenKey.split("|") : []);
    // autoOpenTitles는 autoOpenKey로 대표됨 (배열 identity 변화로 인한 루프 방지)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching, autoOpenKey]);


  const toggleMenu = (title: string) => {
    setOpenMenus((prev) =>
      prev.includes(title)
        ? prev.filter((t) => t !== title)
        : [...prev, title]
    );
  };

  const isActive = (href: string) => location.pathname === href;
  const isParentActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some((child) =>
        child.href ? location.pathname.startsWith(child.href) : false
      );
    }
    return false;
  };

  const handleLinkClick = (menuId?: number) => {
    // 검색 중 클릭이면 검색 클릭 로그 기록 (실패 무시)
    if (isSearching && typeof menuId === "number") {
      menuSearchService.logClick(menuId, searchQuery.trim()).catch(() => {});
    }
    // Close mobile sidebar on navigation
    setSidebarOpen(false);
  };

  // 특정 메뉴에 뱃지 표시 (결재 대기, 요청 서류, SLA 면책, HR 가입 승인 등)
  const getBadgeCount = (href?: string): number | null => {
    if (href === "/ea/inbox" && eaCounts?.to_approve) {
      return eaCounts.to_approve;
    }
    if (href === "/ea/processing" && eaCounts?.processing) {
      return eaCounts.processing;
    }
    if (href === "/document-requests/my" && docRequestPendingCount?.count) {
      return docRequestPendingCount.count;
    }
    if (href === "/my-tasks" && slaExemptionRequests?.length) {
      return slaExemptionRequests.length;
    }
    if (href === "/admin/task-delay-management" && slaExemptionRequests?.length) {
      return slaExemptionRequests.length;
    }
    if (href === "/hr/employees" && hrApprovalStats?.pending_count) {
      return hrApprovalStats.pending_count;
    }
    if (href === "/task-orders" && receivedActionableCount + reviewPendingCount > 0) {
      return receivedActionableCount + reviewPendingCount;
    }
    if (href === "/compliance/roadmap" && complianceRoadmapPending?.count) {
      return complianceRoadmapPending.count;
    }
    if (href === "/compliance/deadlines" && legalDeadlinePending?.count) {
      return legalDeadlinePending.count;
    }
    if (href === "/proposals/projects" && proposalPending?.count) {
      return proposalPending.count;
    }
    if (
      href === "/admin/performance" &&
      performanceDistributionPending?.count
    ) {
      return performanceDistributionPending.count;
    }
    if (href === "/attendance/leave" && leavePromotionActionCount > 0) {
      return leavePromotionActionCount;
    }
    if (href === "/offboarding/admin" && offboardingAdminPending?.count) {
      return offboardingAdminPending.count;
    }
    if (href === "/discipline/my" && disciplineMyPending?.count) {
      return disciplineMyPending.count;
    }
    if (href === "/business-trips" && businessTripPendingReports?.count) {
      return businessTripPendingReports.count;
    }
    if (href === "/worklog" && dailyReportStatus?.enabled && dailyReportStatus.badge_count) {
      return dailyReportStatus.badge_count;
    }
    return null;
  };

  // 메뉴 배지 렌더: 업무하달은 '받은 처리(빨강)'·'검토 대기(앰버)'를 분리 표기, 그 외 메뉴는 단일 배지
  const renderNavBadges = (href: string | undefined, badgeClassName: string) => {
    const fmt = (n: number) => (n > 99 ? "99+" : n);

    if (href === "/task-orders") {
      const segments: {
        count: number;
        title: string;
        variant: "destructive" | "secondary";
        className?: string;
      }[] = [];
      if (receivedActionableCount > 0) {
        segments.push({
          count: receivedActionableCount,
          title: `받은 처리 ${receivedActionableCount}건`,
          variant: "destructive",
        });
      }
      if (reviewPendingCount > 0) {
        segments.push({
          count: reviewPendingCount,
          title: `검토 대기 ${reviewPendingCount}건`,
          variant: "secondary",
          className: "bg-orange-500 text-white hover:bg-orange-600",
        });
      }
      if (segments.length === 0) return null;
      return (
        <span className="flex items-center gap-1">
          {segments.map((seg, idx) => (
            <Badge
              key={idx}
              variant={seg.variant}
              title={seg.title}
              className={cn(badgeClassName, seg.className)}
            >
              {fmt(seg.count)}
            </Badge>
          ))}
        </span>
      );
    }

    const count = getBadgeCount(href);
    if (count === null || count <= 0) return null;
    return (
      <Badge variant="destructive" className={badgeClassName}>
        {fmt(count)}
      </Badge>
    );
  };

  // 상위 메뉴의 뱃지 합계 (하위 메뉴들의 뱃지 재귀 합산)
  const getParentBadgeCount = (children?: NavItem[]): number => {
    if (!children) return 0;
    return children.reduce((sum, child) => {
      const count = getBadgeCount(child.href);
      return sum + (count || 0) + getParentBadgeCount(child.children);
    }, 0);
  };

  const renderNavItem = (item: NavItem) => {
    if (item.children) {
      // 검색 필터가 있을 때는 자동 펼침만 사용, 없을 때는 수동 토글 + 현재 경로
      const isOpen = isSearching
        ? openMenus.includes(item.title)
        : (openMenus.includes(item.title) || isParentActive(item));

      if (sidebarCollapsed) {
        const collapsedBadgeCount = getParentBadgeCount(item.children);
        return (
          <TooltipProvider key={item.title}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => toggleMenu(item.title)}
                  className={cn(
                    "relative flex w-full items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                    isParentActive(item) && "bg-accent text-accent-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {collapsedBadgeCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                      {collapsedBadgeCount > 99 ? "99+" : collapsedBadgeCount}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{item.title}</span>
                    {(() => {
                      const roleBadgeInfo = getRoleBadgeInfo(item.requiredRoles);
                      return roleBadgeInfo && (
                        <Badge
                          variant={roleBadgeInfo.variant}
                          className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", roleBadgeInfo.className)}
                        >
                          {roleBadgeInfo.label}
                        </Badge>
                      );
                    })()}
                  </div>
                  {item.children.map((child) => {
                    const childRoleBadgeInfo = getRoleBadgeInfo(child.requiredRoles);
                    return (
                      <Link
                        key={child.href || child.title}
                        to={child.href!}
                        onClick={() => handleLinkClick(child.id)}
                        className={cn(
                          "flex items-center gap-2 text-sm hover:underline",
                          isActive(child.href!) && "font-semibold text-primary"
                        )}
                      >
                        <span>{child.title}</span>
                        {childRoleBadgeInfo && (
                          <Badge
                            variant={childRoleBadgeInfo.variant}
                            className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", childRoleBadgeInfo.className)}
                          >
                            {childRoleBadgeInfo.label}
                          </Badge>
                        )}
                        {renderNavBadges(child.href, "h-4 px-1 text-[10px]")}
                      </Link>
                    );
                  })}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }

      const parentBadgeCount = getParentBadgeCount(item.children);
      const roleBadgeInfo = getRoleBadgeInfo(item.requiredRoles);

      return (
        <Collapsible
          key={item.title}
          open={isOpen}
          onOpenChange={() => toggleMenu(item.title)}
        >
          <CollapsibleTrigger asChild>
            <button
              className={cn(
                "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                isParentActive(item) && "bg-accent/50 text-accent-foreground",
                item.isActive === false && "opacity-50" // 비활성 스타일 (펼치기는 가능하도록)
              )}
            >
              <div className="flex items-center gap-2">
                <item.icon className="h-5 w-5" />
                <span>{item.title}</span>
                {roleBadgeInfo && (
                  <Badge
                    variant={roleBadgeInfo.variant}
                    className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", roleBadgeInfo.className)}
                  >
                    {roleBadgeInfo.label}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {parentBadgeCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="h-5 min-w-[20px] px-1.5 text-xs"
                  >
                    {parentBadgeCount > 99 ? "99+" : parentBadgeCount}
                  </Badge>
                )}
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="ml-4 mt-1 space-y-1 border-l pl-4">
              {item.children.map((child) => {
                // 3depth 메뉴 지원
                if (child.children && child.children.length > 0) {
                  // 검색 필터가 있을 때는 자동 펼침만 사용, 없을 때는 수동 토글 + 현재 경로
                  const isChildOpen = isSearching
                    ? openMenus.includes(child.title)
                    : (openMenus.includes(child.title) || isParentActive(child));
                  const childBadgeCount = getParentBadgeCount(child.children);
                  const childRoleBadgeInfo = getRoleBadgeInfo(child.requiredRoles);

                  return (
                    <Collapsible
                      key={child.title}
                      open={isChildOpen}
                      onOpenChange={() => toggleMenu(child.title)}
                    >
                      <CollapsibleTrigger asChild>
                        <button
                          className={cn(
                            "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                            isParentActive(child) && "bg-accent/30 text-accent-foreground",
                            child.isActive === false && "opacity-50" // 비활성 스타일 (펼치기는 가능하도록)
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <child.icon className="h-4 w-4" />
                            <span>{child.title}</span>
                            {childRoleBadgeInfo && (
                              <Badge
                                variant={childRoleBadgeInfo.variant}
                                className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", childRoleBadgeInfo.className)}
                              >
                                {childRoleBadgeInfo.label}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {childBadgeCount > 0 && (
                              <Badge
                                variant="destructive"
                                className="h-4 min-w-[16px] px-1 text-[10px]"
                              >
                                {childBadgeCount > 99 ? "99+" : childBadgeCount}
                              </Badge>
                            )}
                            {isChildOpen ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </div>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-4 mt-1 space-y-1 border-l pl-4">
                          {child.children.map((subChild) => {
                            const subRoleBadgeInfo = getRoleBadgeInfo(subChild.requiredRoles);

                            // 비활성 메뉴는 Link 대신 div로 렌더링
                            if (subChild.isActive === false) {
                              return (
                                <div
                                  key={subChild.href || subChild.title}
                                  className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground opacity-50 cursor-not-allowed"
                                >
                                  <div className="flex items-center gap-2">
                                    <subChild.icon className="h-4 w-4" />
                                    <span className="text-xs">{subChild.title}</span>
                                    {subRoleBadgeInfo && (
                                      <Badge
                                        variant={subRoleBadgeInfo.variant}
                                        className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", subRoleBadgeInfo.className)}
                                      >
                                        {subRoleBadgeInfo.label}
                                      </Badge>
                                    )}
                                  </div>
                                  {renderNavBadges(subChild.href, "h-4 min-w-[16px] px-1 text-[10px]")}
                                </div>
                              );
                            }

                            return (
                              <Link
                                key={subChild.href || subChild.title}
                                to={subChild.href!}
                                onClick={() => handleLinkClick(subChild.id)}
                                className={cn(
                                  "flex items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                                  isActive(subChild.href!) &&
                                    "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <subChild.icon className="h-4 w-4" />
                                  <span className="text-xs">{subChild.title}</span>
                                  {subRoleBadgeInfo && (
                                    <Badge
                                      variant={subRoleBadgeInfo.variant}
                                      className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", subRoleBadgeInfo.className)}
                                    >
                                      {subRoleBadgeInfo.label}
                                    </Badge>
                                  )}
                                </div>
                                {renderNavBadges(subChild.href, "h-4 min-w-[16px] px-1 text-[10px]")}
                              </Link>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                }

                // 2depth 일반 메뉴 (children 없음)
                const childRoleBadgeInfo = getRoleBadgeInfo(child.requiredRoles);

                // 비활성 메뉴는 Link 대신 div로 렌더링
                if (child.isActive === false) {
                  return (
                    <div
                      key={child.href || child.title}
                      className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground opacity-50 cursor-not-allowed"
                    >
                      <div className="flex items-center gap-2">
                        <child.icon className="h-4 w-4" />
                        <span>{child.title}</span>
                        {childRoleBadgeInfo && (
                          <Badge
                            variant={childRoleBadgeInfo.variant}
                            className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", childRoleBadgeInfo.className)}
                          >
                            {childRoleBadgeInfo.label}
                          </Badge>
                        )}
                      </div>
                      {renderNavBadges(child.href, "h-5 min-w-[20px] px-1.5 text-xs")}
                    </div>
                  );
                }

                return (
                  <Link
                    key={child.href || child.title}
                    to={child.href!}
                    onClick={() => handleLinkClick(child.id)}
                    className={cn(
                      "flex items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                      isActive(child.href!) &&
                        "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <child.icon className="h-4 w-4" />
                      <span>{child.title}</span>
                      {childRoleBadgeInfo && (
                        <Badge
                          variant={childRoleBadgeInfo.variant}
                          className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", childRoleBadgeInfo.className)}
                        >
                          {childRoleBadgeInfo.label}
                        </Badge>
                      )}
                    </div>
                    {renderNavBadges(child.href, "h-5 min-w-[20px] px-1.5 text-xs")}
                  </Link>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    if (sidebarCollapsed) {
      const roleBadgeInfo = getRoleBadgeInfo(item.requiredRoles);
      return (
        <TooltipProvider key={item.href || item.title}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={item.href!}
                onClick={() => handleLinkClick(item.id)}
                className={cn(
                  "flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                  isActive(item.href!) &&
                    "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className="flex items-center gap-2">
                <span>{item.title}</span>
                {roleBadgeInfo && (
                  <Badge
                    variant={roleBadgeInfo.variant}
                    className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", roleBadgeInfo.className)}
                  >
                    {roleBadgeInfo.label}
                  </Badge>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    const roleBadgeInfo = getRoleBadgeInfo(item.requiredRoles);
    return (
      <Link
        key={item.href || item.title}
        to={item.href!}
        onClick={() => handleLinkClick(item.id)}
        className={cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
          isActive(item.href!) &&
            "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
        )}
      >
        <item.icon className="h-5 w-5" />
        <span>{item.title}</span>
        {roleBadgeInfo && (
          <Badge
            variant={roleBadgeInfo.variant}
            className={cn("h-3.5 px-1 text-[9px] font-semibold leading-none", roleBadgeInfo.className)}
          >
            {roleBadgeInfo.label}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div
        className={cn(
          "flex h-16 items-center border-b px-4",
          sidebarCollapsed && "justify-center px-2"
        )}
      >
        <Link to={getDefaultLandingPath()} className="flex items-center gap-2">
          {companyLogo ? (
            <img
              src={companyLogo}
              alt={companyName}
              className={cn(
                "object-contain",
                sidebarCollapsed ? "h-8 w-8" : "h-10"
              )}
            />
          ) : (
            <>
              <Building2 className={cn("text-primary", sidebarCollapsed ? "h-6 w-6" : "h-7 w-7")} />
              {!sidebarCollapsed && (
                <span className="font-semibold text-lg">{companyName}</span>
              )}
            </>
          )}
        </Link>
      </div>

      {/* Menu Search */}
      {!sidebarCollapsed && (
        <div className="px-3 py-3 border-b">
          <MenuSearchBar onSearchChange={handleSearchChange} />
        </div>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">{navItems.map(renderNavItem)}</nav>

        {/* 검색 중: 결과 없음 안내 */}
        {isSearching && navItems.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground text-center">
            검색 결과가 없습니다
          </p>
        )}
      </ScrollArea>
    </div>
  );
}
